<!--
  StatementSheet.vue
  콜봇 월 정산 · 정산 탭의 '정산서' 표 — 화면 명세 2-3·2-4 를 목업 Detail.dc.html 모양으로.

  Columns: 항목 | 사용량 | 단가 | 금액. Seven items (요금표 v70 + 상담사 번호 추가): 월 사용료 · 120분 초과 통화 · 발신 ·
  웹 텍스트 · 회선 · 상담사 번호 추가 · 일회성 항목, then 공급가액 / 부가세 10% / 청구 합계(부가세 포함).
  - Each item always shows its calculation basis (child rows, indented and lighter, never folded) — what the old CostBreakdownTable sub-rows and the review
    form's 계산 근거 showed: 월 사용료 VAT 환산·개통 달 일할·포함 내용, 120분 전화·웹 음성·포함 통화·초과 계산식, 발신 건당·
    받은 통화, 웹 텍스트 원가, 회선별 일할·포함 회선·판매가 미정, 상담사 번호 구간·계산식·청구 창·매시 기록 없는 날·
    가장 많았던 날. 일회성 항목 has no basis rows (its list is the 일회성 항목 section below).
    회선 rows pair usage lines with charge items by number (settlementRules.pairLines, same as the '원가 · 마진' table).
    A human-entered amount turns its '계산' row into '계산값 근거'. 발신 keeps the calls cancelled before dialing
    (settlementRules.outboundCountText).
  - Review state (useStatementReview `editable`): every item amount has '고치기' (its own narrow trailing column, so the
    amounts stay flush right; other states keep four columns) → inline amount + memo + '계산값으로'.
    Edited items show '수정됨 · 계산값 X원'; items not saved yet show '저장 안 됨'. An empty amount (no default rate) opens
    its editor by itself. Totals follow the typed amounts.
  - Other states: snapshot amounts (`statement.lines.*.amount`, else `charge.lines.*.amount`; the counselor-number item reads
    0 when absent). A human-entered amount (`overridden`) shows '수정됨 · 계산값 X원' (memo as tooltip).
  - No default rate (`rates`/`charge` null): amounts '—' with a link to the default-rate page; with human amounts only
    (manual-only) those amounts and the total show. v70-legacy statements: no item amounts (old keys) — only 일회성 항목
    and the totals saved back then; the row says what to do first ([다시 계산], or [재수정] → [다시 계산] when confirmed). Internal/test companies: every amount 0원.
  - Rate source: only '예외' gets a pill (기본 is the normal case and the header chip already says it).
  - Rows, cell text, snapshot amounts, notice and totals are built by service/statementSheetModel.js (pure — the Excel export
    reuses the same rows). This component keeps only the review editor (form amounts) and the focus/flash behavior.

  props: detail · month · review(useStatementReview) · summary(settlementRules.summaryLine(detail))
  exposes: focusLine(key) — scroll to the item, open its editor and focus the amount input
-->
<template>
  <section class="sheet" :aria-labelledby="`${uid}-title`">
    <header class="sheet__head">
      <h2 :id="`${uid}-title`" class="sheet__title">정산서</h2>
      <span class="sheet__sub">공급가액 · 부가세 별도 · 월 사용료만 VAT 포함 금액에서 환산</span>
    </header>

    <p v-if="notice" class="sheet__notice" :class="{ 'is-warn': notice.warn }" role="status">
      <span>{{ notice.text }}</span>
      <router-link v-if="notice.link" :to="ratesLocation" class="sheet__link">{{ notice.link }}</router-link>
    </p>

    <div class="sheet__scroll" role="region" aria-label="정산서 표(가로로 스크롤)" tabindex="0">
      <table class="sheet__table">
        <caption class="sheet__sr">정산서 — 항목별 사용량·단가·금액</caption>
        <thead>
          <tr>
            <th scope="col">항목</th>
            <th scope="col">사용량</th>
            <th scope="col">단가</th>
            <th scope="col" class="is-r">금액</th>
            <th v-if="hasFixColumn" scope="col" class="sheet__fix-col"><span class="sheet__sr">고치기</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="review.legacy.value" class="sheet__legacy">
            <td :colspan="colCount">{{ legacyRowText }}</td>
          </tr>
          <template v-for="row in rows" :key="row.key">
            <tr
              :ref="(el) => setRowEl(row.key, el)"
              class="sheet__row"
              :class="{ 'is-flash': flash === row.key, 'is-muted': row.muted }"
            >
              <th scope="row" class="sheet__name">
                <span class="sheet__name-main">{{ row.label }}</span>
              </th>
              <td class="sheet__usage">
                <span>{{ row.usage }}</span>
                <span v-if="row.usageSub" class="sheet__line-sub">{{ row.usageSub }}</span>
              </td>
              <td class="sheet__unit">
                <span :class="{ 'is-missing': row.unitMissing, 'is-dim': row.unitDim }">{{ row.unit }}</span>
                <span v-if="row.unitNote" class="sheet__dim">{{ row.unitNote }}</span>
                <span v-if="row.override" class="sheet__pill is-override">예외</span>
                <span v-if="row.unitSub" class="sheet__line-sub">{{ row.unitSub }}</span>
              </td>
              <td class="sheet__amount">
                <!-- Inline editor (검토 필요) -->
                <div v-if="row.editing" class="sheet__editor">
                  <a-input-number
                    :id="`${uid}-${row.key}-amount`"
                    :value="row.formAmount"
                    class="sheet__input"
                    :min="0"
                    :step="1000"
                    :formatter="formatThousandsInput"
                    :parser="parseThousandsInput"
                    addon-after="원"
                    :placeholder="isNumber(row.estimate) ? '' : '금액 입력'"
                    :disabled="!!review.busy.value"
                    :aria-label="`${row.label} 금액`"
                    @update:value="(v) => review.setAmount(row.key, v)"
                  />
                  <a-input
                    :value="row.formMemo"
                    class="sheet__memo"
                    size="small"
                    :maxlength="200"
                    placeholder="메모(선택)"
                    :aria-label="`${row.label} 메모`"
                    :disabled="!!review.busy.value"
                    @update:value="(v) => review.setLineMemo(row.key, v)"
                  />
                  <span class="sheet__editor-actions">
                    <span class="sheet__estimate">{{ estimateText(row) }}</span>
                    <button
                      v-if="row.edited && isNumber(row.estimate)"
                      type="button"
                      class="sheet__fix"
                      :disabled="!!review.busy.value"
                      @click="review.resetLine(row.key)"
                    >
                      계산값으로
                    </button>
                    <button
                      v-if="isNumber(row.formAmount)"
                      type="button"
                      class="sheet__fix"
                      @click="review.closeLine(row.key)"
                    >
                      접기
                    </button>
                  </span>
                </div>
                <template v-else>
                  <a-tooltip :title="row.memo || undefined">
                    <span class="sheet__value" :class="{ 'is-dim': row.amountDim, 'has-tip': !!row.memo }">
                      {{ row.amountText }}
                    </span>
                  </a-tooltip>
                  <span v-if="row.editedText" class="sheet__edited">{{ row.editedText }}</span>
                  <span v-if="row.unsaved" class="sheet__unsaved">저장 안 됨</span>
                </template>
              </td>
              <!-- Own trailing column so every amount stays flush right -->
              <td v-if="hasFixColumn" class="sheet__fix-cell">
                <button
                  v-if="row.fixable && !row.editing"
                  type="button"
                  class="sheet__fix"
                  :aria-label="`${row.label} 금액 고치기`"
                  :disabled="!!review.busy.value"
                  @click="openEditor(row.key)"
                >
                  고치기
                </button>
              </td>
            </tr>
            <!-- Basis rows are always shown under their item (never folded) -->
            <tr v-for="(child, i) in row.children" :key="`${row.key}.${i}`" class="sheet__child">
              <th scope="row" class="sheet__child-name">
                <span :title="child.tip || undefined">{{ child.name }}</span>
                <a-tooltip v-if="child.pill" :title="child.pill.tooltip">
                  <span class="sheet__pill is-warn">{{ child.pill.label }}</span>
                </a-tooltip>
              </th>
              <td>{{ child.usage }}</td>
              <td v-if="child.detail !== undefined" :colspan="hasFixColumn ? 3 : 2" class="sheet__child-detail">
                {{ child.detail }}
              </td>
              <template v-else>
                <td>
                  {{ child.unit }}
                  <span v-if="child.override" class="sheet__pill is-override">예외</span>
                </td>
                <td class="is-r">{{ child.amount }}</td>
                <td v-if="hasFixColumn"></td>
              </template>
            </tr>
          </template>
        </tbody>
        <tfoot>
          <tr class="sheet__foot is-supply">
            <th scope="row" colspan="3">
              공급가액
              <span v-if="internalNote" class="sheet__dim">{{ internalNote }}</span>
            </th>
            <td class="is-r">{{ formatWon(foot.supply) }}</td>
            <td v-if="hasFixColumn"></td>
          </tr>
          <tr class="sheet__foot">
            <th scope="row" colspan="3">
              부가세 10%
              <span class="sheet__dim">원 미만 버림</span>
            </th>
            <td class="is-r">{{ formatWon(foot.vat) }}</td>
            <td v-if="hasFixColumn"></td>
          </tr>
          <tr class="sheet__foot is-total">
            <th scope="row" colspan="3">청구 합계 (부가세 포함)</th>
            <td class="is-r">{{ formatWon(foot.withVat) }}</td>
            <td v-if="hasFixColumn"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, ref } from 'vue';

import { formatThousandsInput, formatWon, isNumber, parseThousandsInput } from '../../service/settlementFormat';
import { SETTLEMENT_ROUTE_NAMES, STATEMENT_LINE_KEYS } from '../../service/settlementRules';
import { buildStatementSheet, sheetItemView } from '../../service/statementSheetModel';

const props = defineProps({
  /** 정산 상세(요청 계약 2) */
  detail: { type: Object, required: true },
  /** 'YYYY-MM' — 회선 일할의 그 달 일수 */
  month: { type: String, required: true },
  /** useStatementReview() 의 반환값 — 검토 필요 입력과 합계 */
  review: { type: Object, required: true },
  /** settlementRules.summaryLine(detail) — 검토 필요가 아닐 때의 합계 */
  summary: { type: Object, required: true },
});

const uid = `st-sheet-${getCurrentInstance()?.uid ?? 0}`;

const legacy = computed(() => props.review.legacy.value);
const editable = computed(() => props.review.editable.value);
/** '고치기' gets its own narrow trailing column in review only — other states keep four columns */
const hasFixColumn = computed(() => editable.value);
const colCount = computed(() => (hasFixColumn.value ? 5 : 4));

/**
 * Rows, notice and totals come from the pure model (service/statementSheetModel.js — the Excel export reuses it). In review
 * the items the operator changed turn their '계산' basis row into '계산값 근거', like a saved human amount does.
 */
const sheet = computed(() =>
  buildStatementSheet(props.detail, {
    month: props.month,
    summary: props.summary,
    editedKeys: editable.value ? STATEMENT_LINE_KEYS.filter((key) => props.review.items.value[key]?.edited) : null,
  })
);

/** v70-legacy row — a confirmed month is reopened before it can be recalculated */
const legacyRowText = computed(() => sheet.value.legacyText);

const ratesLocation = computed(() => ({ name: SETTLEMENT_ROUTE_NAMES.rates, query: { month: props.month } }));

const notice = computed(() => sheet.value.notice);

const internalNote = computed(() => sheet.value.internalNote);

// ---------------------------------------------------------------------------
// Amount of an item in the review editor (the model holds the snapshot amounts)
// ---------------------------------------------------------------------------

/**
 * Display state of one item's amount cell while the statement is in review.
 * @returns {{ amountText: string, amountDim: boolean, editedText: string, memo: string, unsaved: boolean,
 *   editing: boolean, fixable: boolean, formAmount: unknown, formMemo: string, estimate: number|null, edited: boolean }}
 */
function reviewAmountCell(key) {
  const item = props.review.items.value[key];
  const empty = !isNumber(item.amount);
  let editedText = '';
  // Without a default rate the server estimate (0) is not a computed value, and the notice above the table already
  // says there is none — just mark the edited items
  if (item.edited) editedText = props.review.noRates.value ? '수정됨' : `수정됨 · 계산값 ${formatWon(item.estimate)}`;
  return {
    amountText: empty ? '금액 입력' : formatWon(item.amount),
    amountDim: empty,
    editedText,
    memo: (item.memo ?? '').trim(),
    unsaved: item.unsaved,
    editing: item.open || empty,
    fixable: true,
    formAmount: item.amount,
    formMemo: item.memo,
    estimate: item.estimate,
    edited: item.edited,
  };
}

function estimateText(row) {
  if (props.review.noRates.value) return `계산값 없음(${legacy.value ? 'v70 이전 정산서' : '기본 요율 없음'})`;
  return `계산값 ${formatWon(row.estimate)}`;
}

// ---------------------------------------------------------------------------
// Model rows → template rows (service/statementSheetModel.js sheetItemView — the unit test checks the same function)
// ---------------------------------------------------------------------------

const rows = computed(() =>
  sheet.value.items.map((item) => {
    const view = sheetItemView(item);
    // 일회성 항목 has no editor — its amount is the one-off list total
    if (!editable.value || !STATEMENT_LINE_KEYS.includes(item.key)) return view;
    const cell = reviewAmountCell(item.key);
    return { ...view, ...cell, muted: cell.amountText === '0원' && !cell.editing };
  })
);

/** Footer totals — review follows the typed amounts, the rest reads the summary */
const foot = computed(() => {
  if (editable.value) {
    const t = props.review.totals.value;
    return { supply: t?.total ?? null, vat: t?.vat ?? null, withVat: t?.totalWithVat ?? null };
  }
  const [supply, vat, withVat] = sheet.value.totals;
  return { supply: supply.amount.value, vat: vat.amount.value, withVat: withVat.amount.value };
});

// ---------------------------------------------------------------------------
// Edit / focus
// ---------------------------------------------------------------------------

const rowEls = {};
function setRowEl(key, el) {
  if (el) rowEls[key] = el;
  else delete rowEls[key];
}

const flash = ref('');
let flashTimer = null;

async function openEditor(key) {
  props.review.openLine(key);
  await nextTick();
  rowEls[key]?.querySelector?.('input')?.focus({ preventScroll: true });
}

/** Warning resolution '… 금액 고치기' — scroll to the item, open its editor and focus the amount */
async function focusLine(key) {
  if (!STATEMENT_LINE_KEYS.includes(key)) return;
  props.review.openLine(key);
  await nextTick();
  const el = rowEls[key];
  if (!el) return;
  el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  el.querySelector?.('input')?.focus({ preventScroll: true });
  flash.value = key;
  clearTimeout(flashTimer);
  flashTimer = setTimeout(() => {
    flash.value = '';
  }, 2200);
}

onBeforeUnmount(() => clearTimeout(flashTimer));

defineExpose({ focusLine });
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.sheet {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  color: $st-text;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 4px 12px;
    padding: 16px 20px 0;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
  }

  &__sub {
    font-size: 12.5px;
    color: $st-muted;
  }

  &__notice {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    margin: 0 20px;
    padding: 8px 12px;
    border-radius: 8px;
    background: $st-bg;
    font-size: 12.5px;
    color: $st-text-secondary;

    &.is-warn {
      background: $st-warning-bg;
      color: $st-warning;
    }
  }

  &__link {
    font-weight: 600;
  }

  &__scroll {
    overflow-x: auto;

    &:focus-visible {
      outline: 2px solid $st-accent-border;
    }
  }

  &__table {
    width: 100%;
    min-width: 600px;
    border-collapse: collapse;
    font-size: 14px;
    font-variant-numeric: tabular-nums;

    th,
    td {
      padding: 12px 16px;
      // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
      border-bottom: 1px solid $st-border !important;
      text-align: left;
      vertical-align: top;
    }

    thead th {
      padding: 9px 16px;
      background: $st-bg;
      font-size: 12.5px;
      font-weight: 600;
      color: $st-text-secondary;
      white-space: nowrap;
    }

    .is-r {
      text-align: right;
    }
  }

  &__sr {
    @include st-sr-only;
  }

  &__legacy td {
    color: $st-warning;
    font-size: 13px;
  }

  &__row {
    transition: background-color 0.3s ease;

    &.is-flash td,
    &.is-flash th {
      background: $st-accent-bg;
    }

    &.is-muted .sheet__value {
      color: $st-muted;
    }
  }

  &__name {
    font-weight: 600;
    white-space: nowrap;
  }

  &__name-main {
    display: block;
  }

  &__usage,
  &__unit {
    color: $st-text;
  }

  &__line-sub {
    display: block;
    margin-top: 2px;
    font-size: 12.5px;
    color: $st-muted;
  }

  &__dim {
    margin-left: 4px;
    font-size: 12.5px;
    font-weight: 400;
    color: $st-muted;
  }

  .is-missing {
    color: $st-warning;
    font-weight: 600;
  }

  .is-dim {
    color: $st-muted;
  }

  &__pill {
    @include st-pill;
    margin-left: 6px;
    vertical-align: 1px;

    &.is-override {
      @include st-pill-tone($st-override, $st-override-bg, $st-override-border);
    }

    &.is-warn {
      @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
      cursor: help;
    }
  }

  &__amount {
    text-align: right !important;
    white-space: nowrap;
  }

  &__value {
    font-weight: 600;

    &.has-tip {
      border-bottom: 1px dotted $st-border-strong;
      cursor: help;
    }
  }

  &__fix-col {
    width: 1%;
  }

  &__fix-cell {
    width: 1%;
    padding-left: 0 !important;
    white-space: nowrap;
  }

  &__fix {
    height: auto; // override the global button { height: 40px }
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 12.5px;
    color: $st-accent;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:disabled {
      color: $st-muted;
      cursor: default;
      text-decoration: none;
    }

    &:focus-visible {
      outline: 2px solid $st-accent-border;
    }
  }

  &__edited {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: $st-override;
  }

  &__unsaved {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: $st-warning;
  }

  &__editor {
    display: inline-flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    width: 220px;
    text-align: left;
  }

  &__input {
    width: 100%;
  }

  &__editor-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 10px;
    white-space: normal;
  }

  &__estimate {
    margin-right: auto;
    font-size: 12px;
    color: $st-muted;
  }

  &__child {
    th,
    td {
      padding-top: 7px;
      padding-bottom: 7px;
      background: $st-bg;
      font-size: 13px;
      color: $st-text-secondary;
    }
  }

  &__child-name {
    padding-left: 32px !important;
    font-weight: 400;
    white-space: nowrap;
  }

  &__child-detail {
    white-space: normal;
  }

  // Korean words never split mid-word — lines break at spaces only
  &__child td,
  &__usage,
  &__unit {
    word-break: keep-all;
    overflow-wrap: break-word;
  }

  &__foot {
    th,
    td {
      padding-top: 6px;
      padding-bottom: 6px;
      border-bottom: 0 !important;
      font-weight: 600;
    }

    &.is-supply th,
    &.is-supply td {
      padding-top: 14px;
    }

    &.is-total th,
    &.is-total td {
      padding-top: 12px;
      padding-bottom: 12px;
      background: $st-bg;
      font-size: 16px;
      font-weight: 700;
    }
  }
}
</style>
